/* eslint-disable no-undef */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { Button, Icon } from '@pwc/pwc-deals-ui';

import { USER_ROLES, NavigationNodeId } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import featureFlags from 'common/helpers/featureFlags';

import AvatarWithMenu from '../../../common/components/AvatarWithMenu/index';
import ContentActionsRowMenu from '../../../common/components/ContentActions/ContentActionsRowMenu/ContentActionsRowMenu';
import PillContainer from '../../../common/components/Pills/PillContainer';
import useTablet from '../../../common/hooks/useTablet';
import useMobile from '../../../common/hooks/useMobile';
import useTabletL from '../../../common/hooks/useTabletL';
import { viewHypothesisPath } from '../../../common/services/urlService';
import TagList from '../../analyses/common/TagList/TagList';
import { sanitize } from '../../../common/services/sanitizeService';
import { removeBreakspaces } from '../../../common/services/textService';
import Thumbnail from '../Thumbnail/Thumbnail';
import { AnalysesAndInsightsTable } from './AnalysesAndInsightsTable';
import './HypothesisGridCard.scss';
import CalloutBox from '../../../common/components/CalloutBox/CalloutBox';
import { LastUpdatedLabel } from './LastUpdatedLabel';
import Separator, {
  SeparatorOrientation,
} from '../../../common/components/Separator';
import { getProject } from '../../project/projectSlice';

const HypothesisGridCard = ({
  hypothesis,
  projectId,
  onEditMenuClick,
  onDeleteMenuClick,
  index,
  allRetrievedConnections,
}) => {
  const isTablet = useTablet();
  const isTabletL = useTabletL();
  const isMobile = useMobile();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { navigation } = useSelector(getProject);

  const [showDetails, setShowDetails] = useState(false);

  const history = useHistory();
  const { t } = useTranslation('');

  const navigateToDetailView = useCallback(() => {
    const path = viewHypothesisPath(projectId, hypothesis.contentThreadId);
    history.push(path);
  }, [projectId, hypothesis.contentThreadId, history]);

  const handleShowMore = () => {
    setShowDetails((state) => !state);
  };

  const isAnalysisVisible = () => {
    const item = navigation?.find(
      (nav) => nav.id === NavigationNodeId.ANALYSES
    );

    return item ? !item.isHidden : true;
  };

  useEffect(() => {
    setShowDetails(!isTablet);
  }, [isTablet]);

  return (
    <div className="card-container" data-testid={`card-container-${index}`}>
      <div
        className="hypothesis-card__contents"
        data-testid="hypothesis-card-contents"
      >
        <div
          className="hypothesis-card__contents__details"
          data-testid="kfa-card-details"
        >
          <div
            className="hypothesis-card__contents__details__title"
            data-testid="kfa-card-title"
          >
            <Link
              to={viewHypothesisPath(projectId, hypothesis.contentThreadId)}
              data-testid={`hypothesis-detail-grid-link-${index}`}
              aria-label="View Key Focus Area"
            >
              <h2>{hypothesis.title}</h2>
            </Link>
            <AvatarWithMenu
              contentThreadId={hypothesis.contentThreadId}
              showInfoLockingUser
            >
              <ContentActionsRowMenu
                projectId={projectId}
                itemId={hypothesis.contentThreadId}
                onEditMenuClick={onEditMenuClick}
                onDeleteMenuClick={onDeleteMenuClick}
                direction="left"
                isEditDisabled={isMobile}
              />
            </AvatarWithMenu>
          </div>

          {isTablet && (
            <Button
              className={classnames(
                'button-no-appearance full-width show-more-btn mb-3',
                { buttonClass: isTabletL }
              )}
              onClick={handleShowMore}
            >
              <span className="d-flex justify-content-between align-items-center">
                {showDetails ? t('showLessKFA') : t('showMoreKFA')}
                <Icon
                  name={showDetails ? 'desc' : 'asc'}
                  width={18}
                  className="fill icon-orange-theme"
                />
              </span>
            </Button>
          )}

          {showDetails && (
            <>
              <div className="kfa-summary-content-wrapper">
                <div className="kfa-summary-content-wrapper__left-col">
                  {hypothesis.insight && (
                    <div className="hypothesis-card__contents__insight-snippet">
                      {hypothesis.insight.description && (
                        <CalloutBox
                          iconName="light-bulb"
                          iconClass="icon fill icon-grey-theme"
                          headerLabel={t('insight')}
                          text={hypothesis.insight.description}
                          dataTestId="insight"
                          usage="summary"
                        />
                      )}
                    </div>
                  )}
                  {hypothesis.body && (
                    <div className="hypothesis-card__contents__insight-description">
                      <span
                        className="card-description"
                        data-testid="kfa-description"
                        dangerouslySetInnerHTML={{
                          __html: sanitize(removeBreakspaces(hypothesis.body)),
                        }}
                      />
                    </div>
                  )}

                  <div className="hypothesis-card__contents__details__button-container">
                    <Button
                      className="button-link"
                      onClick={navigateToDetailView}
                    >
                      {t('readMore')}
                    </Button>
                  </div>
                </div>
                <div className="kfa-summary-content-wrapper__right-col">
                  {hypothesis.insight && hypothesis.insight.items && (
                    <Thumbnail
                      projectId={projectId}
                      thumbnail={hypothesis.insight.items[0]}
                      editable={false}
                      showAutoSync={false}
                      onSummary
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {showDetails && isAnalysisVisible() && (
        <div
          className="hypothesis-card__contents__analyses-list"
          data-testid="hypothesis-card-analyses"
        >
          <AnalysesAndInsightsTable
            projectId={projectId}
            hypothesis={hypothesis}
            hypothesisIndex={index}
            allRetrievedConnections={allRetrievedConnections}
            sortByDate={false}
          />
        </div>
      )}
      <div
        className="hypothesis-grid-card-footer"
        data-testid="hypothesis-grid-card-footer"
      >
        {hypothesis.tags && hypothesis.tags.length > 0 && (
          <>
            <TagList tags={hypothesis.tags} testId="hypothesis-tag-list" />
            {!isTablet && (
              <Separator orientation={SeparatorOrientation.Vertical} />
            )}
          </>
        )}
        <PillContainer
          allowDraftPill
          className="align-center"
          content={hypothesis}
          hideTextVersion={featureFlags.isVersioningPhase1Enabled}
          projectId={projectId}
        />
        {isPwcUser && (
          <div className="last-updated-label-container">
            <LastUpdatedLabel
              date={hypothesis.dateModified}
              userName={hypothesis.modifiedBy}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default HypothesisGridCard;
